/**
 * On web, we can use Intl.DateTimeFormat with timeZone option to get the local timezone. On the app, Hermes doesn't support Intl.DateTimeFormat with timeZone option. This is a shared package that should work on React and React Native. The workaround is to let the host app set the timezone string. For React Native, we can use the react-native-localize package to get the timezone string.
 *
 * This file provides utility functions to parse and format dates in the user's local timezone.
 *
 * Usage:
 *
 * ```javascript
 * import { initTimeZone } from '@groupthinkai/groupthink';
 * import * as RNLocalize from 'react-native-localize';
 *
 * initTimeZone(RNLocalize.getTimeZone());
 * ```
 *
 * Important: parse and calculate dates in UTC, _then_ use goto() to set the timezone prior to formatting or comparing locale-sensitive dates (e.g. "Are two dateStrings on the same day in the user's local timezone?").
 */

import spacetime, { TimeUnit } from 'spacetime';

let _defaultTZ;

export function initTimeZone(tzString) {
  _defaultTZ = tzString;
}

/**
 * If the host device has not initialized the timezone string, we can fallback to the Intl.DateTimeFormat with timeZone option. If that is not available, we can fallback to UTC.
 */
function getTimeZone() {
  if (_defaultTZ) return _defaultTZ;
  const intl = Intl.DateTimeFormat?.().resolvedOptions()?.timeZone;
  return intl || 'UTC';
}

export const parseInLocalZone = (dateInput: string | Date) =>
  spacetime(dateInput).goto(getTimeZone());

const localSpacetimeDate = (dateString: string) => parseInLocalZone(dateString);

export const localNativeDate = (dateString: string) =>
  localSpacetimeDate(dateString).toNativeDate();

export const nowInLocalNativeDate = () => spacetime.now().goto(getTimeZone()).toNativeDate();

export const isSameLocalDay = (a: string | Date, b: string | Date) => isSameLocal(a, b, 'day');

function isSameLocal(a: string | Date, b: string | Date, unit: TimeUnit) {
  const zone = getTimeZone();
  const sA = spacetime(a).goto(zone);
  const sB = spacetime(b).goto(zone);
  return sA.isSame(sB, unit);
}

/**
 * Returns true if "now" in the user's time-zone shares the same year as the provided date (also interpreted in the user's time-zone).
 */
export function isCurrentLocalYear(dateInput: string | Date): boolean {
  const zone = getTimeZone();
  const target = spacetime(dateInput).goto(zone);
  const today = spacetime.now(zone);
  return target.year() === today.year();
}

/**
 * Returns true if the given date (interpreted in the user's local zone) falls on the same calendar day as "now" in that zone.
 */
export function isLocalToday(dateInput: string | Date): boolean {
  const zone = getTimeZone();
  const dt = spacetime(dateInput).goto(zone);
  const today = spacetime.now(zone);
  return dt.isSame(today, 'day');
}
